/**
 * Trick play rules and resolution for Armenian Belot (32 cards).
 *
 * Trick index `1..8` (32-card deal) and the +10 bonus on the last trick are owned by the orchestrator (package F);
 * scoring (162/130, +10) is package E.
 *
 * @module game/belot/armenian/tricks
 */

const {
  cardSuit,
  cardRank,
  compareCardsForTrick,
  trickStrengthForRank,
  isValidCard,
} = require("./types");

/**
 * @typedef {{ seat: number, card: number }} BelotTrickPlay
 */

/**
 * Points for one card toward the round card total (not including the +10 last-trick bonus).
 * Trump: J 20, 9 14, A 11, 10 10, K 4, Q 3, 7/8 0.
 * Non-trump: J 2, 9 0, A 11, 10 10, K 4, Q 3, 7/8 0.
 *
 * @param {number} card
 * @param {number|null} contractTrump
 * @returns {number}
 */
function cardPointsTowardRoundTotal(card, contractTrump) {
  const r = cardRank(card);
  const isTrump =
    contractTrump === "all_trump"
      ? true
      : contractTrump != null && cardSuit(card) === contractTrump;
  if (r === 3) return isTrump ? 20 : 2; // J
  if (r === 2) return isTrump ? 14 : 0; // 9
  if (r === 7) return 11; // A
  if (r === 6) return 10; // 10
  if (r === 5) return 4; // K
  if (r === 4) return 3; // Q
  return 0; // 7, 8
}

/**
 * Sum {@link cardPointsTowardRoundTotal} for cards in one trick (no +10).
 *
 * @param {number[]} cards
 * @param {number|null} contractTrump
 * @returns {number}
 */
function trickCardsPointsSum(cards, contractTrump) {
  let s = 0;
  for (const c of cards) s += cardPointsTowardRoundTotal(c, contractTrump);
  return s;
}

/**
 * @param {BelotTrickPlay[]} trickSoFar
 * @param {number} trumpSuit
 * @returns {number} max trump trick-strength on table; `-1` if none
 */
function maxTrumpStrengthInTrick(trickSoFar, trumpSuit) {
  let m = -1;
  for (const p of trickSoFar) {
    if (cardSuit(p.card) === trumpSuit) {
      m = Math.max(m, trickStrengthForRank(cardRank(p.card), true));
    }
  }
  return m;
}

/**
 * Max plain (non-trump) trick-strength among cards of `leadSuit` already on table.
 * @param {BelotTrickPlay[]} trickSoFar
 * @param {number} leadSuit
 * @returns {number}
 */
function maxPlainStrengthInLeadSuit(trickSoFar, leadSuit) {
  let m = -1;
  for (const p of trickSoFar) {
    if (cardSuit(p.card) === leadSuit) {
      m = Math.max(m, trickStrengthForRank(cardRank(p.card), false));
    }
  }
  return m;
}

/**
 * Strongest trump card(s) that beat `minStrengthExclusive`; if none, returns `null`.
 * When non-null, returns a one-element array (unique card in 32-deck).
 *
 * @param {number[]} handTrumpCards
 * @param {number} minStrengthExclusive
 * @returns {number[]|null}
 */
function mandatoryOvertrumpPlay(handTrumpCards, minStrengthExclusive) {
  /** @type {number[]} */
  const beating = [];
  for (const c of handTrumpCards) {
    const st = trickStrengthForRank(cardRank(c), true);
    if (st > minStrengthExclusive) beating.push(c);
  }
  if (beating.length === 0) return null;
  let best = beating[0];
  let bestS = trickStrengthForRank(cardRank(best), true);
  for (let i = 1; i < beating.length; i += 1) {
    const c = beating[i];
    const s = trickStrengthForRank(cardRank(c), true);
    if (s > bestS) {
      best = c;
      bestS = s;
    }
  }
  return [best];
}

/**
 * Must follow in plain suit with a higher card than any plain card of that suit on table (Armenian rule).
 * @param {number[]} handLeadCards cards in hand matching lead suit
 * @param {number} minStrengthExclusive
 * @returns {number[]|null}
 */
function mandatoryHigherPlainPlay(handLeadCards, minStrengthExclusive) {
  /** @type {number[]} */
  const beating = [];
  for (const c of handLeadCards) {
    const st = trickStrengthForRank(cardRank(c), false);
    if (st > minStrengthExclusive) beating.push(c);
  }
  if (beating.length === 0) return null;
  let best = beating[0];
  let bestS = trickStrengthForRank(cardRank(best), false);
  for (let i = 1; i < beating.length; i += 1) {
    const c = beating[i];
    const s = trickStrengthForRank(cardRank(c), false);
    if (s > bestS) {
      best = c;
      bestS = s;
    }
  }
  return [best];
}

/**
 * @param {number[]} hand
 * @param {BelotTrickPlay[]} trickSoFar plays in order; empty = opening lead
 * @param {number|null} contractTrump suit or `null` (no-trump)
 * @param {number} leadSeat seat that led this trick (unused when `trickSoFar` empty; reserved for orchestrator)
 * @returns {number[]} distinct legal cards (usually one card when overtrump forces uniqueness)
 */
function legalPlays(hand, trickSoFar, contractTrump, leadSeat) {
  void leadSeat;
  if (!Array.isArray(hand)) throw new TypeError("tricks.legalPlays: hand must be an array");
  for (const c of hand) {
    if (!isValidCard(c)) throw new Error("tricks.legalPlays: invalid card in hand");
  }
  if (!Array.isArray(trickSoFar)) {
    throw new TypeError("tricks.legalPlays: trickSoFar must be an array");
  }
  for (const p of trickSoFar) {
    if (!p || typeof p.seat !== "number" || !isValidCard(p.card)) {
      throw new Error("tricks.legalPlays: invalid trick play");
    }
  }

  if (trickSoFar.length === 0) {
    return hand.slice();
  }

  const leadSuit = cardSuit(trickSoFar[0].card);
  const inLead = hand.filter((c) => cardSuit(c) === leadSuit);
  const isAllTrump = contractTrump === "all_trump";

  if (inLead.length > 0) {
    if (
      isAllTrump ||
      (contractTrump != null && leadSuit === contractTrump)
    ) {
      const maxT = isAllTrump
        ? maxTrumpStrengthInTrick(
            trickSoFar.filter((p) => cardSuit(p.card) === leadSuit),
            leadSuit,
          )
        : maxTrumpStrengthInTrick(trickSoFar, contractTrump);
      const forced = mandatoryOvertrumpPlay(inLead, maxT);
      return forced != null ? forced : inLead.slice();
    }
    const maxPlain = maxPlainStrengthInLeadSuit(trickSoFar, leadSuit);
    const forcedPlain = mandatoryHigherPlainPlay(inLead, maxPlain);
    return forcedPlain != null ? forcedPlain : inLead.slice();
  }

  if (!isAllTrump && contractTrump != null) {
    const tr = hand.filter((c) => cardSuit(c) === contractTrump);
    if (tr.length > 0) {
      const maxT = maxTrumpStrengthInTrick(trickSoFar, contractTrump);
      if (maxT >= 0) {
        const forced = mandatoryOvertrumpPlay(tr, maxT);
        return forced != null ? forced : tr.slice();
      }
      return tr.slice();
    }
  }

  return hand.slice();
}

/**
 * @param {BelotTrickPlay[]} playsInOrder exactly one entry per seat in trick, in play order
 * @param {number|null} trump `null` = no-trump deal
 * @returns {number} winning seat
 */
function trickWinner(playsInOrder, trump) {
  if (!Array.isArray(playsInOrder) || playsInOrder.length === 0) {
    throw new Error("tricks.trickWinner: non-empty plays required");
  }
  const leadSuit = cardSuit(playsInOrder[0].card);
  let best = playsInOrder[0];
  for (let i = 1; i < playsInOrder.length; i += 1) {
    const p = playsInOrder[i];
    if (compareCardsForTrick(p.card, best.card, leadSuit, trump) > 0) {
      best = p;
    }
  }
  return best.seat;
}

module.exports = {
  cardPointsTowardRoundTotal,
  trickCardsPointsSum,
  legalPlays,
  trickWinner,
};
